import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api.js";
import {
  Badge, Button, EmptyState, LoadingRows, Notice, PageHeader, SelectField,
} from "../components/ui.jsx";

function dateLabel(value) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("en", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

function label(value) {
  return String(value || "").replaceAll("_", " ");
}

function tone(status) {
  if (["resolved", "dismissed"].includes(status)) return "succeeded";
  if (status === "new") return "pending";
  return "active";
}

export default function SignalsPage({ onOpenCompany, onOpenLead, onCountChange }) {
  const [filters, setFilters] = useState({ status: "attention", priority: "" });
  const [state, setState] = useState({ loading: true, signals: [], summary: {}, error: "" });
  const [acting, setActing] = useState("");

  const load = useCallback(async () => {
    setState((current) => ({ ...current, loading: true, error: "" }));
    try {
      const data = await api.signals({ status: filters.status, ...(filters.priority ? { priority: filters.priority } : {}), limit: 200 });
      setState({ loading: false, signals: data.signals, summary: data.summary, error: "" });
      onCountChange?.(data.summary.attention_count);
    } catch (error) {
      setState((current) => ({ ...current, loading: false, error: error.message }));
    }
  }, [filters, onCountChange]);

  useEffect(() => { load(); }, [load]);

  async function update(signal, input) {
    setActing(signal.id);
    try { await api.updateSignal(signal.id, input); await load(); }
    catch (error) { setState((current) => ({ ...current, error: error.message })); }
    finally { setActing(""); }
  }

  const counts = useMemo(() => ({
    attention: Number(state.summary.attention_count || 0),
    fresh: Number(state.summary.new_count || 0),
    high: Number(state.summary.high_priority_count || 0),
  }), [state.summary]);

  return (
    <div className="grid gap-8">
      <PageHeader eyebrow="Communications" title="Signals" description="One action queue for replies and updates received through email, WhatsApp, LinkedIn, meetings, calls, and other connected agents." action={<Button onClick={load}>Refresh</Button>} />
      {state.error && <Notice>{state.error}</Notice>}

      <section className="grid overflow-hidden rounded-xl bg-card shadow-soft sm:grid-cols-3" aria-label="Signal summary">
        {[["Needs attention", counts.attention], ["New", counts.fresh], ["High priority", counts.high]].map(([name, value]) => (
          <div className="grid min-h-24 content-center gap-1 border-b border-neutral-100 px-5 last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0" key={name}>
            <span className="text-[10px] font-bold uppercase tracking-[.1em] text-muted-foreground">{name}</span>
            <strong className="text-2xl tabular-nums tracking-[-.04em]">{value}</strong>
          </div>
        ))}
      </section>

      <section className="grid gap-4">
        <div className="grid gap-3 rounded-xl bg-card p-4 shadow-soft sm:grid-cols-[220px_220px_auto] sm:items-end">
          <SelectField label="Queue" value={filters.status} onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}>
            <option value="attention">Needs attention</option><option value="new">New</option><option value="acknowledged">Acknowledged</option>
            <option value="in_progress">In progress</option><option value="waiting">Waiting</option><option value="resolved">Resolved</option><option value="all">All signals</option>
          </SelectField>
          <SelectField label="Priority" value={filters.priority} onChange={(event) => setFilters((current) => ({ ...current, priority: event.target.value }))}>
            <option value="">All priorities</option><option value="urgent">Urgent</option><option value="high">High</option><option value="normal">Normal</option><option value="low">Low</option>
          </SelectField>
          {(filters.priority || filters.status !== "attention") && <Button variant="ghost" onClick={() => setFilters({ status: "attention", priority: "" })}>Clear filters</Button>}
        </div>

        {state.loading ? <LoadingRows count={5} /> : !state.signals.length ? (
          <EmptyState title="No signals in this queue" description="New inbound updates will appear here automatically. Connected agents can also push communication events through MCP." />
        ) : (
          <div className="grid gap-3">{state.signals.map((signal) => (
            <article className="grid gap-4 rounded-xl bg-card p-5 shadow-soft lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center" key={signal.id}>
              <div className="grid min-w-0 gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge status={tone(signal.status)}>{label(signal.status)}</Badge>
                  <Badge status={["urgent", "high"].includes(signal.priority) ? "failed" : undefined}>{signal.priority}</Badge>
                  {signal.channel && <Badge>{signal.channel}</Badge>}
                  {!signal.company_id && !signal.person_id && !signal.sales_motion_id && <Badge status="pending">Needs linking</Badge>}
                </div>
                <div><h2 className="text-base font-bold tracking-[-.01em]">{signal.title}</h2><p className="mt-1 max-w-[80ch] whitespace-pre-wrap text-[13px] leading-5 text-muted-foreground">{signal.summary || "No message preview"}</p></div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                  <span>{dateLabel(signal.occurred_at)}</span>
                  {signal.company_name && <button className="cursor-pointer border-0 bg-transparent p-0 font-semibold text-primary hover:underline" type="button" onClick={() => onOpenCompany?.(signal.company_id)}>{signal.company_name}</button>}
                  {signal.sales_motion_name && <button className="cursor-pointer border-0 bg-transparent p-0 font-semibold text-primary hover:underline" type="button" onClick={() => onOpenLead?.(signal.sales_motion_id)}>{signal.sales_motion_name}</button>}
                  {signal.person_name && <span>{signal.person_name}</span>}
                  {signal.assigned_to && <span>Assigned to {signal.assigned_to.name}</span>}
                </div>
                {signal.suggested_action && <p className="text-xs font-medium text-neutral-600">Next: {signal.suggested_action}</p>}
              </div>
              {!(["resolved", "dismissed"].includes(signal.status)) && <div className="flex flex-wrap gap-2 lg:justify-end">
                {signal.status === "new" && <Button disabled={acting === signal.id} onClick={() => update(signal, { status: "acknowledged" })}>{acting === signal.id ? "Saving…" : "Acknowledge"}</Button>}
                {["acknowledged", "waiting"].includes(signal.status) && <Button disabled={acting === signal.id} onClick={() => update(signal, { status: "in_progress" })}>Start work</Button>}
                <Button variant="primary" disabled={acting === signal.id} onClick={() => update(signal, { status: "resolved", resolution_note: "Reviewed and actioned in RevPal." })}>{acting === signal.id ? "Saving…" : "Resolve"}</Button>
              </div>}
            </article>
          ))}</div>
        )}
      </section>
    </div>
  );
}
